'use client'

import { useEffect, useState } from 'react'
import {
  EnrollmentUniversities,
  SantaClaraUniversityName,
} from '@/utils/enrollment-helpers'
import useEnrollmentStore from '@cerifi/state/enrollment-store'
import { useUniversityStore } from '@cerifi/state/university.store'
import { InfoIcon } from '@cerifi/ui/components/icons'
import { Select, SelectItem } from '@cerifi/ui/components/select'
import Tooltip from '@cerifi/ui/components/tooltip'

/**
 * communicates directly with the Enrollment Store to update the user's chosen university with bigcommerce
 */
export const UniversityEnrollmentPicker = () => {
  const [university, changeUniversity, init, showValidation] =
    useEnrollmentStore((state) => [
      state.university,
      state.changeUniversity,
      state.init,
      state.showValidation,
    ])
  const [universityName] = useUniversityStore((state) => [state.universityName])
  const [universities, setUniversities] = useState(EnrollmentUniversities)

  useEffect(() => init(), [init])

  /* University name will exist if they are on an university microsite, this will force them
  to choose that university and disable choosing a different option
  */
  useEffect(() => {
    if (universityName) {
      changeUniversity(universityName)

      if (universityName === SantaClaraUniversityName) {
        /** Santa Clara should be hidden from the dropdown in general, but if the user
         * is coming from the Santa Clara microsite it still needs to be selected.
         */
        setUniversities(
          [...EnrollmentUniversities].concat([SantaClaraUniversityName])
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universityName])

  const [tooltipOpen, setTooltipOpen] = useState(false)

  return (
    <>
      <label className="mb-3 flex items-center gap-2">
        <span className="font-medium">University of your choice</span>{' '}
        <Tooltip
          onOpenChange={(o) => setTooltipOpen(o)}
          open={tooltipOpen}
          trigger={
            <button
              aria-label="more information"
              onClick={() => setTooltipOpen(true)}
            >
              <InfoIcon fill="black" stroke="white" />
            </button>
          }
        >
          Not sure? Select the University that you&apos;ll be most proud of to
          frame and display your certification upon completion - with zero
          impact on course content or cost.
        </Tooltip>
      </label>

      <div className="mb-6 max-w-[44rem]">
        You will receive your certificate from any of these accredited
        universities, and the course content remains the same regardless of your
        selection.
      </div>
      <div className="max-w-[23.125rem]">
        <Select
          invalid={showValidation && !university}
          errorMessage="Please pick a university"
          data-testid="univeristy-picker"
          aria-label="University"
          placeholder="Select University"
          disabled={
            !!universityName /** User is on an university microsite, they cant choose a different university */
          }
          value={university}
          onValueChange={(v) => changeUniversity(v)}
        >
          {universities.map((u) => (
            <SelectItem value={u} key={u} data-testid={`${u}`}>
              {u}
            </SelectItem>
          ))}
        </Select>
      </div>
    </>
  )
}
